import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, MoreVerticalIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import styles from '../../_css/ContenetManagement.module.css';
import { getAllTutorials, deleteTutorial, publishTutorial, unpublishTutorial } from "../../requests";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer.js";
import { notifySuccess, notifyError } from "../../utils/helper";
import DeleteTutorialModal from "../Modals/DeleteTutorialModal";
import PublishTutorialModal from "../Modals/PublishTutorialModal";
import EditTutorialModal from "../Modals/EditTutorialModal";
import WatchVideoModal from "../Modals/WatchVideoModal";

const Tutorials = ({ dataFetch }) => {
    const dispatch = useDispatch();
    const [statusFilter, setStatusFilter] = useState('All');
    const [contentTypeFilter, setContentTypeFilter] = useState('All');
    const [sortOption, setSortOption] = useState('Latest First');
    const [menuOpenIndex, setMenuOpenIndex] = useState(null);
    const [showSortDropdown, setSortDropdown] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showContentTypeDropdown, setShowContentTypeDropdown] = useState(false);
    const [tutorials, setTutorials] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Refs for click outside detection
    const sortDropdownRef = useRef(null);
    const statusDropdownRef = useRef(null);
    const contentTypeDropdownRef = useRef(null);
    const menuDropdownRefs = useRef([]);

    // Handling modals
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [tutorialToDelete, setTutorialToDelete] = useState(null);
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [tutorialToPublish, setTutorialToPublish] = useState(null);
    const [publishAction, setPublishAction] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [tutorialToEdit, setTutorialToEdit] = useState(null);
    const [showWatchModal, setShowWatchModal] = useState(false); // Watch modal state
    const [tutorialToWatch, setTutorialToWatch] = useState(null); // Tutorial to watch

    // Fetch tutorials from API
    const fetchTutorials = async () => {
        try {
            dispatch(startLoader());
            setLoading(true);
            const response = await getAllTutorials();
            if (response && response.data) {
                setTutorials(response.data);
            }
        } catch (error) {
            console.error('Error fetching tutorials:', error);
            notifyError("Failed to fetch tutorials");
        } finally {
            dispatch(stopLoader());
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTutorials();
    }, [dispatch]);

    useEffect(() => {
        if (dataFetch) {
            fetchTutorials();
        }
    }, [dataFetch]);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Close sort dropdown
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
                setSortDropdown(false);
            }

            // Close status dropdown
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }

            // Close content type dropdown
            if (contentTypeDropdownRef.current && !contentTypeDropdownRef.current.contains(event.target)) {
                setShowContentTypeDropdown(false);
            }

            // Close menu dropdowns
            if (menuOpenIndex !== null) {
                const menuRef = menuDropdownRefs.current[menuOpenIndex];
                if (menuRef && !menuRef.contains(event.target)) {
                    setMenuOpenIndex(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuOpenIndex]);

    const toggleSortDropdown = () => setSortDropdown(!showSortDropdown);
    const toggleStatusDropdown = () => setShowStatusDropdown(!showStatusDropdown);
    const toggleContentTypeDropdown = () => setShowContentTypeDropdown(!showContentTypeDropdown);

    // Filter options
    const statusOptions = ['All', 'Published', 'Draft'];
    const contentTypeOptions = ['Video', 'Read Only'];
    const sortOptions = ['Latest First', 'Oldest First'];

    const filteredTutorials = tutorials
        .filter(tutorial => {
            // Status filter
            if (statusFilter !== 'All' && tutorial.status !== statusFilter.toLowerCase()) {
                return false;
            }

            // Content type filter
            if (contentTypeFilter !== 'All' && tutorial.filterType !== contentTypeFilter.toLowerCase()) {
                return false;
            }

            return true;
        })
        .sort((a, b) => {
            if (sortOption === 'Latest First') {
                return new Date(b.createdAt || b.id) - new Date(a.createdAt || a.id);
            } else {
                return new Date(a.createdAt || a.id) - new Date(b.createdAt || b.id);
            }
        });

    const toggleMenu = (index, e) => {
        e.stopPropagation();
        setMenuOpenIndex(menuOpenIndex === index ? null : index);
    };

    // Handle watch tutorial
    const handleWatchTutorial = (tutorial) => {
        console.log("Clicked")
        setShowWatchModal(true);
        setTutorialToWatch(tutorial);

    };

    const handleMenuAction = async (action, tutorial) => {
        try {
            dispatch(startLoader());
            setLoading(true);

            switch (action) {
                case 'Watch':
                    // Handle watch action - open watch modal
                    handleWatchTutorial(tutorial);
                    break;

                case 'Edit':
                    // Handle edit action - open edit modal
                    handleEditTutorial(tutorial);
                    break;

                case 'Delete':
                    setTutorialToDelete(tutorial);
                    setShowDeleteModal(true);
                    break;

                case 'Publish':
                    setTutorialToPublish(tutorial);
                    setPublishAction('publish');
                    setShowPublishModal(true);
                    break;
                case 'Unpublish':
                    setTutorialToPublish(tutorial);
                    setPublishAction('unpublish');
                    setShowPublishModal(true);
                    break;
                case 'View':
                    // Handle watch action - open watch modal
                    navigate('/readonlyview', {
                        state: {
                            viewData: tutorial
                        }
                    });
                    break;

                default:
                    break;
            }
        } catch (error) {
            console.error(`Error performing ${action} action:`, error);
            notifyError(error.response?.data?.message || `Failed to ${action} tutorial`);
        } finally {
            dispatch(stopLoader());
            setLoading(false);
            setMenuOpenIndex(null);
        }
    };

    // Handle edit tutorial
    const handleEditTutorial = (tutorial) => {
        setTutorialToEdit(tutorial);
        setShowEditModal(true);
    };

    // Handle edit success
    const handleEditSuccess = () => {
        setShowEditModal(false);
        setTutorialToEdit(null);
        fetchTutorials();
        notifySuccess("Tutorial updated successfully!");
    };

    const handleDeleteConfirm = async () => {
        if (!tutorialToDelete) return;

        try {
            dispatch(startLoader());
            await deleteTutorial(tutorialToDelete.id);
            await fetchTutorials();
            notifySuccess("Tutorial deleted successfully!");
            handleModalClose();
        } catch (error) {
            console.error("Error deleting tutorial:", error);
            notifyError(error.response?.data?.message || "Failed to delete tutorial");
        } finally {
            dispatch(stopLoader());
        }
    };

    const handlePublishConfirm = async () => {
        if (!tutorialToPublish) return;

        try {
            dispatch(startLoader());
            if (publishAction === 'publish') {
                await publishTutorial(tutorialToPublish.id);
            } else {
                await unpublishTutorial(tutorialToPublish.id);
            }
            await fetchTutorials();
            notifySuccess(`Tutorial ${publishAction === 'publish' ? 'published' : 'unpublished'} successfully!`);
            handleModalClose();
        } catch (error) {
            console.error(`Error ${publishAction}ing tutorial:`, error);
            notifyError(error.response?.data?.message || `Failed to ${publishAction} tutorial`);
        } finally {
            dispatch(stopLoader());
        }
    };


    const handleModalClose = () => {
        setShowPublishModal(false);
        setShowDeleteModal(false);
        setShowEditModal(false);
        setShowWatchModal(false); // Close watch modal
        setTutorialToPublish(null);
        setTutorialToDelete(null);
        setTutorialToEdit(null);
        setTutorialToWatch(null); // Clear watch tutorial
        setPublishAction('');
        setMenuOpenIndex(null);
    };

    const handleStatusSelect = (option) => {
        setStatusFilter(option);
        setShowStatusDropdown(false);
    };

    const handleContentTypeSelect = (option) => {
        setContentTypeFilter(option);
        setShowContentTypeDropdown(false);
    };

    const handleSortSelect = (option) => {
        setSortOption(option);
        setSortDropdown(false);
    };

    const handleCtaClick = async (tutorial, e) => {
        e.preventDefault();
        console.log("HandleCtaClick: ", tutorial);

        if (tutorial.status === 'draft') {
            await handleMenuAction('Publish', tutorial);
        } else {
            if (tutorial.contentType === 'readOnly' || tutorial.filterType?.toLowerCase() === 'document') {
                navigate('/readonlyview', {
                    state: {
                        viewData: tutorial
                    }
                });
            } else {
                handleWatchTutorial(tutorial);
            }
        }
    };


    const getMenuActions = (tutorial) => {
        const baseActions = ['Edit', 'Delete'];

        if (tutorial.filterType?.toLowerCase() === 'document') {
            if (tutorial.status === 'draft') {
                return [...baseActions, 'Publish', 'View'];
            } else if (tutorial.status === 'published') {
                return [...baseActions, 'View', 'Unpublish'];
            }
        } else if (tutorial.filterType?.toLowerCase() === 'video') {
            if (tutorial.status === 'draft') {
                return [...baseActions, 'Watch', 'Publish'];
            } else if (tutorial.status === 'published') {
                return [...baseActions, 'Watch', 'Unpublish'];
            }
        }

        return baseActions; // fallback
    };


    const getTagStyle = (tag) => {
        if (tag === 'Published') return `${styles.tutorialsTag} ${styles.tutorialsTagPublished}`;
        if (tag === 'Draft') return `${styles.tutorialsTag} ${styles.tutorialsTagDraft}`;
        return `${styles.tutorialsTag} ${styles.tutorialsTagOther}`;
    };

    // Helper function to ensure tags array exists
    const getTutorialTags = (tutorial) => {
        const tags = [];
        // Add status tag
        if (tutorial.status === 'published') {
            tags.push('Published');
        } else {
            tags.push('Draft');
        }
        // Add content type tag
        if (tutorial.filterType) {
            tags.push(tutorial.filterType);
        }
        // Add duration tag if available
        if (tutorial.duration) {
            tags.push(tutorial.duration);
        }
        // Add other tags from API
        if (tutorial.tags && Array.isArray(tutorial.tags)) {
            tutorial.tags.forEach(tag => {
                if (!tags.includes(tag)) {
                    tags.push(tag);
                }
            });
        }

        return tags;
    };

    return (
        <div className={styles.tutorialsContainer}>
            {/* {loading && (
                <div className={styles.tutorialsLoadingOverlay}>
                    Processing...
                </div>
            )} */}

            {/* Filters */}
            <div className={styles.tutorialsFiltersContainer}>
                {/* Sort */}
                <div className={styles.tutorialsFilterDropdownContainer} ref={sortDropdownRef}>
                    <button onClick={toggleSortDropdown} className={styles.tutorialsFilterButton}>
                        {sortOption} <ChevronDown size={16} color="#6b7280" />
                    </button>
                    {showSortDropdown && (
                        <div className={styles.tutorialsDropdown}>
                            {sortOptions.map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => handleSortSelect(item)}
                                    className={styles.tutorialsDropdownItem}
                                >
                                    {item}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Status Filter */}
                <div className={styles.tutorialsFilterDropdownContainer} ref={statusDropdownRef}>
                    <button onClick={toggleStatusDropdown} className={styles.tutorialsFilterButton}>
                        {statusFilter === 'All' ? 'Status' : statusFilter} <ChevronDown size={16} color="#6b7280" />
                    </button>
                    {showStatusDropdown && (
                        <div className={styles.tutorialsDropdown}>
                            {statusOptions.map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => handleStatusSelect(item)}
                                    className={styles.tutorialsDropdownItem}
                                >
                                    {item}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Content Type Filter */}
                <div className={styles.tutorialsFilterDropdownContainer} ref={contentTypeDropdownRef}>
                    <button onClick={toggleContentTypeDropdown} className={styles.tutorialsFilterButton}>
                        {contentTypeFilter === 'All' ? 'Content Type' : contentTypeFilter} <ChevronDown size={16} color="#6b7280" />
                    </button>
                    {showContentTypeDropdown && (
                        <div className={styles.tutorialsDropdown}>
                            {contentTypeOptions.map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => handleContentTypeSelect(item)}
                                    className={styles.tutorialsDropdownItem}
                                >
                                    {item}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Tutorials Grid */}
            <div className={styles.tutorialsGrid}>
                {filteredTutorials.length === 0 ? (
                    <div className={styles.tutorialsEmptyState}>
                        {tutorials.length === 0 ? 'No tutorials found.' : 'No tutorials match the current filters.'}
                    </div>
                ) : (
                    filteredTutorials.map((tutorial, index) => (
                        <div key={tutorial.id} className={styles.tutorialsCard}>
                            {/* Thumbnail */}
                            <div className={styles.tutorialsThumbnailContainer}>
                                {/* Status Badge */}
                                <div className={`${styles.tutorialsStatusBadge} ${tutorial.status === 'published' ? styles.tutorialsStatusPublished : styles.tutorialsStatusDraft
                                    }`}>
                                    {tutorial.status === 'published' ? 'Published' : 'Draft'}
                                </div>

                                <div
                                    ref={el => menuDropdownRefs.current[index] = el}
                                    style={{ position: 'relative' }}
                                >
                                    <div
                                        className={styles.tutorialsMenuButton}
                                        onClick={(e) => toggleMenu(index, e)}
                                    >
                                        <MoreVerticalIcon size={20} color='#1F2937' />
                                    </div>
                                    {menuOpenIndex === index && (
                                        <div className={styles.tutorialsMenuDropdown}>
                                            {getMenuActions(tutorial).map((item, idx) => (
                                                <div
                                                    key={idx}
                                                    onClick={() => handleMenuAction(item, tutorial)}
                                                    className={styles.tutorialsMenuItem}
                                                >
                                                    {item}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <img
                                    src={tutorial.thumbnail || 'https://via.placeholder.com/300x200?text=Tutorial'}
                                    alt={tutorial.title}
                                    className={styles.tutorialsThumbnail}
                                />
                            </div>

                            {/* Title & Description */}
                            <h3 className={styles.tutorialsTitle}>{tutorial.title}</h3>
                            <div className={styles.tutorialsDescription}>
                                {tutorial.description?.includes("<") ? (
                                    // If it looks like HTML
                                    <p>{tutorial.shortSummary}</p>
                                ) : (
                                    // Otherwise show as plain text
                                    <p>{tutorial.description}</p>
                                )}
                            </div>

                            {/* Tags */}
                            <div className={styles.tutorialsTagsContainer}>
                                {getTutorialTags(tutorial).map((tag, idx) => (
                                    <span
                                        key={idx}
                                        className={getTagStyle(tag)}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>

                            {/* CTA */}
                            <button
                                className={styles.tutorialsCtaButton}
                                onClick={(e) => handleCtaClick(tutorial, e)}
                            >
                                {tutorial.filterType?.toLowerCase() === 'document'
                                    ? tutorial.status === 'draft'
                                        ? 'Publish'
                                        : 'View'
                                    : tutorial.filterType?.toLowerCase() === 'video'
                                        ? tutorial.status === 'draft'
                                            ? 'Watch Now'
                                            : 'Watch Now'
                                        : 'Publish'}
                            </button>

                        </div>
                    ))
                )}
            </div>

            {/* Modals */}
            <DeleteTutorialModal
                show={showDeleteModal}
                onHide={handleModalClose}
                onDelete={handleDeleteConfirm}
            />

            <PublishTutorialModal
                show={showPublishModal}
                onHide={handleModalClose}
                onConfirm={handlePublishConfirm}
                action={publishAction}
            />

            {/* Edit Tutorial Modal */}
            <EditTutorialModal
                show={showEditModal}
                onHide={handleModalClose}
                onSuccess={handleEditSuccess}
                tutorialData={tutorialToEdit}
            />

            {/* Watch Video Modal */}
            <WatchVideoModal
                show={showWatchModal}
                onHide={handleModalClose}
                videoData={tutorialToWatch}
            />
        </div>
    );
};

export default Tutorials;